export type CanvasShortcutGroup = 'tools' | 'edit' | 'viewport' | 'panels' | 'other';

export type CanvasMenuPanel = 'resource' | 'storyboard';

export type CanvasResourceLibraryCategory = 'character' | 'location' | 'prop' | 'footage';

/** 资产画布 assetType → 资源库侧栏可对齐的 category；非角色/场景/道具返回 null。 */
export function resolveCanvasAssetResourceCategory(
  assetType: string | null | undefined
): CanvasResourceLibraryCategory | null {
  if (assetType === 'character' || assetType === 'location' || assetType === 'prop') {
    return assetType;
  }
  return null;
}

export type CanvasMenuShortcutTarget =
  | { kind: 'storyboard' }
  | { kind: 'resource'; category: CanvasResourceLibraryCategory };

export type CanvasShortcutActionId =
  | 'hand'
  | 'select'
  | 'space-pan'
  | 'zoom-in'
  | 'zoom-out'
  | 'zoom-100'
  | 'fit-view'
  | 'arrange'
  | 'minimap'
  | 'panel-storyboard'
  | 'panel-character'
  | 'panel-location'
  | 'panel-prop'
  | 'panel-footage'
  | 'escape'
  | 'delete-node'
  | 'shortcuts-help'
  | 'disable-snap'
  | 'undo'
  | 'redo'
  | 'paste-media'
  | 'group'
  | 'ungroup';

export interface CanvasShortcutCatalogItem {
  id: CanvasShortcutActionId;
  group: CanvasShortcutGroup;
  label: string;
  /** Display-only entries (e.g. Space hold) are not registered by the hook. */
  displayOnly?: boolean;
  /** Primary key tokens used by formatCanvasShortcutLabel. Use "Mod" for ⌘/Ctrl, "Alt" for ⌥/Alt. */
  keyTokens: string[];
  /** Extra equivalent shortcuts shown in the same help row, joined with " / ". */
  keyAlternatives?: string[][];
  /** True when this action currently uses a user override (no catalog alternatives). */
  overridden?: boolean;
}

export type CanvasShortcutEvent = Pick<
  KeyboardEvent,
  'key' | 'code' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'repeat'
>;

const MODIFIER_TOKENS = new Set(['Mod', 'Shift', 'Alt']);

let canvasShortcutCatalogReader: (() => readonly CanvasShortcutCatalogItem[] | null) | null = null;

export function setCanvasShortcutCatalogReader(
  reader: (() => readonly CanvasShortcutCatalogItem[] | null) | null
): void {
  canvasShortcutCatalogReader = reader;
}

export function getResolvedCanvasShortcutCatalog(): readonly CanvasShortcutCatalogItem[] {
  return canvasShortcutCatalogReader?.() ?? CANVAS_SHORTCUT_CATALOG;
}

export const CANVAS_SHORTCUT_GROUP_LABELS: Record<CanvasShortcutGroup, string> = {
  tools: '工具模式',
  edit: '编辑',
  viewport: '视口与缩放',
  panels: '侧栏面板',
  other: '其他',
};

export const CANVAS_SHORTCUT_CATALOG: CanvasShortcutCatalogItem[] = [
  { id: 'hand', group: 'tools', label: '抓手模式', keyTokens: ['H'] },
  { id: 'select', group: 'tools', label: '选择模式', keyTokens: ['V'] },
  {
    id: 'space-pan',
    group: 'tools',
    label: '临时抓手（按住）',
    displayOnly: true,
    keyTokens: ['Space'],
  },
  {
    id: 'disable-snap',
    group: 'tools',
    label: '拖动时暂时关闭吸附',
    displayOnly: true,
    keyTokens: ['Alt'],
  },
  { id: 'undo', group: 'edit', label: '撤销', keyTokens: ['Mod', 'Z'] },
  {
    id: 'redo',
    group: 'edit',
    label: '重做',
    keyTokens: ['Mod', 'Shift', 'Z'],
    keyAlternatives: [['Mod', 'Y']],
  },
  {
    id: 'paste-media',
    group: 'edit',
    label: '粘贴图片 / 视频 / 音频',
    // 粘贴由原生 paste 事件处理（要拿 ClipboardEvent 里的 files），不能再被快捷键分发一次。
    displayOnly: true,
    keyTokens: ['Mod', 'V'],
  },
  { id: 'group', group: 'edit', label: '编组', keyTokens: ['Mod', 'G'] },
  { id: 'ungroup', group: 'edit', label: '解组', keyTokens: ['Mod', 'Shift', 'G'] },
  { id: 'zoom-in', group: 'viewport', label: '放大', keyTokens: ['Mod', '+'] },
  { id: 'zoom-out', group: 'viewport', label: '缩小', keyTokens: ['Mod', '-'] },
  { id: 'zoom-100', group: 'viewport', label: '缩放到 100%', keyTokens: ['Mod', '0'] },
  { id: 'fit-view', group: 'viewport', label: '适应画布', keyTokens: ['F'] },
  { id: 'arrange', group: 'viewport', label: '整理画布', keyTokens: ['Shift', 'L'] },
  { id: 'minimap', group: 'viewport', label: '开关小地图', keyTokens: ['M'] },
  { id: 'panel-storyboard', group: 'panels', label: '分镜稿面板', keyTokens: ['1'] },
  { id: 'panel-character', group: 'panels', label: '资源库/角色面板', keyTokens: ['2'] },
  { id: 'panel-location', group: 'panels', label: '场景面板', keyTokens: ['3'] },
  { id: 'panel-prop', group: 'panels', label: '道具面板', keyTokens: ['4'] },
  { id: 'panel-footage', group: 'panels', label: '文件面板', keyTokens: ['5'] },
  { id: 'escape', group: 'other', label: '关闭面板 / 取消选中', keyTokens: ['Esc'] },
  {
    id: 'delete-node',
    group: 'other',
    label: '删除当前激活卡片',
    keyTokens: ['Delete'],
    keyAlternatives: [['Backspace']],
  },
  { id: 'shortcuts-help', group: 'other', label: '快捷键说明', keyTokens: ['?'] },
];

export function isApplePlatform(userAgent = getDefaultUserAgent()): boolean {
  return /Mac|iPhone|iPad|iPod/i.test(userAgent);
}

export function getCanvasModKeyLabel(userAgent = getDefaultUserAgent()): string {
  return isApplePlatform(userAgent) ? '⌘' : 'Ctrl';
}

export function getCanvasAltKeyLabel(userAgent = getDefaultUserAgent()): string {
  return isApplePlatform(userAgent) ? '⌥' : 'Alt';
}

/** Format key tokens for UI, e.g. ["Mod","+"] → "⌘ +" / "Ctrl +"; ["Alt"] → "⌥" / "Alt". */
export function formatCanvasShortcutLabel(
  keyTokens: string[],
  userAgent = getDefaultUserAgent()
): string {
  const modLabel = getCanvasModKeyLabel(userAgent);
  const altLabel = getCanvasAltKeyLabel(userAgent);
  const resolved = keyTokens.map((token) => {
    if (token === 'Mod') return modLabel;
    if (token === 'Alt') return altLabel;
    return token;
  });

  if (resolved.length === 1) return resolved[0];
  if (keyTokens[0] === 'Mod') {
    return `${resolved[0]} ${resolved.slice(1).join(' ')}`;
  }
  return resolved.join('+');
}

/** Format a catalog item, joining alternates with " / ". */
export function formatCanvasShortcutItemKeys(
  item: Pick<CanvasShortcutCatalogItem, 'keyTokens' | 'keyAlternatives'>,
  userAgent = getDefaultUserAgent()
): string {
  const labels = [
    formatCanvasShortcutLabel(item.keyTokens, userAgent),
    ...(item.keyAlternatives ?? []).map((tokens) => formatCanvasShortcutLabel(tokens, userAgent)),
  ];
  return labels.join(' / ');
}

/**
 * 按 action id 取格式化后的按键提示，供工具栏 tooltip 与右键菜单共用。
 * 走 catalog 而不是各处硬编码 keyTokens，避免菜单显示与实际快捷键漂移。
 */
export function getCanvasShortcutKeysLabel(
  actionId: CanvasShortcutActionId,
  userAgent = getDefaultUserAgent(),
  catalog: readonly CanvasShortcutCatalogItem[] = getResolvedCanvasShortcutCatalog()
): string {
  const item = catalog.find((entry) => entry.id === actionId);
  if (!item) return '';
  return formatCanvasShortcutItemKeys(item, userAgent);
}

export function isCanvasShortcutOverridden(
  actionId: CanvasShortcutActionId,
  catalog: readonly CanvasShortcutCatalogItem[] = getResolvedCanvasShortcutCatalog()
): boolean {
  return Boolean(catalog.find((entry) => entry.id === actionId)?.overridden);
}

export function canvasShortcutKbdClassName(overridden: boolean): string {
  return overridden
    ? 'text-brand-default border-brand-default/40'
    : 'text-text-secondary border-border-light';
}

export function getCanvasShortcutCatalogByGroup(
  catalog: CanvasShortcutCatalogItem[] = CANVAS_SHORTCUT_CATALOG
): Array<{ group: CanvasShortcutGroup; label: string; items: CanvasShortcutCatalogItem[] }> {
  const order: CanvasShortcutGroup[] = ['tools', 'edit', 'viewport', 'panels', 'other'];
  return order
    .map((group) => ({
      group,
      label: CANVAS_SHORTCUT_GROUP_LABELS[group],
      items: catalog.filter((item) => item.group === group),
    }))
    .filter((section) => section.items.length > 0);
}

export function menuShortcutFromAction(
  actionId: CanvasShortcutActionId
): CanvasMenuShortcutTarget | null {
  if (actionId === 'panel-storyboard') return { kind: 'storyboard' };
  if (actionId === 'panel-character') return { kind: 'resource', category: 'character' };
  if (actionId === 'panel-location') return { kind: 'resource', category: 'location' };
  if (actionId === 'panel-prop') return { kind: 'resource', category: 'prop' };
  if (actionId === 'panel-footage') return { kind: 'resource', category: 'footage' };
  return null;
}

/** @deprecated Use menuShortcutFromAction */
export function panelFromShortcutAction(actionId: CanvasShortcutActionId): CanvasMenuPanel | null {
  const target = menuShortcutFromAction(actionId);
  if (!target) return null;
  return target.kind === 'storyboard' ? 'storyboard' : 'resource';
}

function getDefaultUserAgent(): string {
  if (typeof navigator === 'undefined') return '';
  return `${navigator.platform} ${navigator.userAgent}`;
}

function hasModKey(event: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'>): boolean {
  return event.metaKey || event.ctrlKey;
}

function keyTokenFromEvent(event: CanvasShortcutEvent): string | null {
  if (event.code === 'Escape' || event.key === 'Escape') return 'Esc';
  if (event.code === 'Delete' || event.key === 'Delete') return 'Delete';
  if (event.code === 'Backspace' || event.key === 'Backspace') return 'Backspace';
  if (event.code === 'Space' || event.key === ' ' || event.key === 'Space') return 'Space';
  if (event.key === '?' || (event.code === 'Slash' && event.shiftKey)) return '?';
  if (event.code === 'Equal' || event.key === '+' || event.key === '=') return '+';
  if (event.code === 'Minus' || event.key === '-' || event.key === '_') return '-';

  const letter = /^Key([A-Z])$/.exec(event.code);
  if (letter) return letter[1];

  const digit = /^Digit([0-9])$/.exec(event.code);
  if (digit) return digit[1];

  if (/^[A-Za-z]$/.test(event.key)) return event.key.toUpperCase();
  if (/^[0-9]$/.test(event.key)) return event.key;
  return null;
}

function keyTokenMatchesEvent(token: string, event: CanvasShortcutEvent): boolean {
  switch (token) {
    case 'Esc':
      return event.code === 'Escape' || event.key === 'Escape';
    case 'Delete':
      return event.code === 'Delete' || event.key === 'Delete';
    case 'Backspace':
      return event.code === 'Backspace' || event.key === 'Backspace';
    case 'Space':
      return event.code === 'Space' || event.key === ' ' || event.key === 'Space';
    case '+':
      return event.code === 'Equal' || event.key === '+' || event.key === '=';
    case '-':
      return event.code === 'Minus' || event.key === '-' || event.key === '_';
    case '?':
      return event.key === '?' || (event.code === 'Slash' && event.shiftKey);
    default:
      break;
  }

  if (/^[A-Z]$/.test(token)) {
    return event.code === `Key${token}`;
  }
  if (/^[0-9]$/.test(token)) {
    return event.code === `Digit${token}` || event.key === token;
  }
  return false;
}

export function tokensMatchEvent(tokens: readonly string[], event: CanvasShortcutEvent): boolean {
  const tokenSet = new Set(tokens);
  const wantsMod = tokenSet.has('Mod');
  const wantsShift = tokenSet.has('Shift');
  const wantsAlt = tokenSet.has('Alt');
  const keyTokens = tokens.filter((token) => !MODIFIER_TOKENS.has(token));
  if (keyTokens.length !== 1) return false;

  const keyToken = keyTokens[0];
  const shiftFlexible = keyToken === '+' || keyToken === '?';

  if (wantsMod !== hasModKey(event)) return false;
  if (wantsAlt !== Boolean(event.altKey)) return false;
  if (shiftFlexible) {
    if (wantsShift && !event.shiftKey) return false;
  } else if (wantsShift !== Boolean(event.shiftKey)) {
    return false;
  }

  return keyTokenMatchesEvent(keyToken, event);
}

export function tokensFromKeyboardEvent(event: CanvasShortcutEvent): string[] {
  const keyToken = keyTokenFromEvent(event);
  const tokens: string[] = [];
  if (hasModKey(event)) tokens.push('Mod');
  if (event.altKey) tokens.push('Alt');
  const omitShift = keyToken === '?' || keyToken === '+';
  if (event.shiftKey && !omitShift) tokens.push('Shift');
  if (keyToken) tokens.push(keyToken);
  return tokens;
}

/**
 * Resolve a keyboard event to a canvas shortcut action.
 * Space is intentionally excluded (handled by useCanvasSpacePanning).
 */
export function matchCanvasShortcutAction(
  event: CanvasShortcutEvent,
  resolvedItems: readonly CanvasShortcutCatalogItem[] = getResolvedCanvasShortcutCatalog()
): Exclude<CanvasShortcutActionId, 'space-pan' | 'disable-snap' | 'paste-media'> | null {
  // 缩放允许按住连续触发；其余单键动作避免键盘自动重复造成反复切换或重复整理。
  if (event.repeat && !hasModKey(event)) return null;

  for (const item of resolvedItems) {
    if (item.displayOnly) continue;
    const chords = item.overridden
      ? [item.keyTokens]
      : [item.keyTokens, ...(item.keyAlternatives ?? [])];
    if (chords.some((tokens) => tokensMatchEvent(tokens, event))) {
      return item.id as Exclude<
        CanvasShortcutActionId,
        'space-pan' | 'disable-snap' | 'paste-media'
      >;
    }
  }

  return null;
}
